import {
  Eraser,
  ExternalLink,
  KeyRound,
  Leaf,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { UserAvatar } from "@/components/user-avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useAdminAreas,
  useAdminGroups,
  useAdminStats,
  useAdminUsers,
  useAnonymiseUser,
  useCreateArea,
  useCreateUser,
  useResetPassword,
  useUpdateArea,
  useUpdateUser,
} from "@/hooks/use-admin";
import { useDeleteGroup } from "@/hooks/use-groups";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { useAuth } from "@/hooks/use-auth";
import { t } from "@/i18n/pl";
import {
  formatDays,
  formatMessageTime,
  formatTime,
  phoneInput,
  townLabel,
} from "@/lib/format";
import { queryClient } from "@/lib/query-client";
import { ApiError } from "@/services/api-client";
import * as adminService from "@/services/admin.service";
import type { AccessRequest, GroupSummary, PickupArea, User } from "@/types/api";


function RequestRow({ request }: { request: AccessRequest }) {
  const [approveOpen, setApproveOpen] = useState(false);
  // Phone-handover fallback: set when the approval e-mail could not be sent.
  const [fallbackPassword, setFallbackPassword] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "access-requests"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
  };
  const closeFallback = () => {
    setFallbackPassword(null);
    void queryClient.invalidateQueries({ queryKey: ["admin", "access-requests"] });
  };
  const approve = useMutation({
    mutationFn: () => adminService.approveAccessRequest(request.id),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setApproveOpen(false);
      if (result.email_sent) {
        toast.success(t.admin.approvedEmailSent);
        void queryClient.invalidateQueries({ queryKey: ["admin", "access-requests"] });
      } else {
        // Refetching the list now would unmount this row and the dialog with
        // it, so the requests query refreshes when the dialog closes instead.
        setFallbackPassword(result.initial_password);
      }
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : t.common.genericError),
  });
  const reject = useMutation({
    mutationFn: () => adminService.rejectAccessRequest(request.id),
    onSuccess: () => {
      invalidate();
      toast.success(t.admin.rejected);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : t.common.genericError),
  });

  return (
    <li className="bg-card flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border p-3">
      {/* Not a user yet: initials straight from the name on the form. */}
      <UserAvatar user={request} className="size-10" fallbackClassName="bg-secondary font-semibold" />
      <div className="min-w-0 flex-1 basis-40">
        <p className="truncate text-sm font-semibold">{request.full_name}</p>
        <p className="text-muted-foreground truncate text-xs">
          {request.email} · {request.phone}
        </p>
        <p className="text-muted-foreground/70 text-xs">
          {t.admin.requestedAt(formatMessageTime(request.created_at))}
        </p>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => setApproveOpen(true)}>
          {t.admin.approve}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={reject.isPending}
          onClick={() => reject.mutate()}
        >
          {t.admin.reject}
        </Button>
      </div>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.admin.approveTitle(request.full_name)}</DialogTitle>
            <DialogDescription>{t.admin.approveBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button disabled={approve.isPending} onClick={() => approve.mutate()}>
              {t.admin.approve}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={fallbackPassword !== null}
        onOpenChange={(open) => {
          if (!open) closeFallback();
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.admin.passwordFallbackTitle}</DialogTitle>
            <DialogDescription>{t.admin.passwordFallbackBody}</DialogDescription>
          </DialogHeader>
          <div className="bg-muted rounded-lg p-3 text-sm">
            <p className="text-muted-foreground text-xs">{t.admin.loginLabel}</p>
            <p className="mb-2 font-medium">{request.email}</p>
            <p className="text-muted-foreground text-xs">{t.admin.initialPassword}</p>
            <p className="font-mono text-base font-semibold tracking-wide">{fallbackPassword}</p>
          </div>
          <DialogFooter>
            <Button onClick={closeFallback}>{t.common.close}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  );
}

function Stat({ label, count, icon }: { label: string; count: number; icon?: ReactNode }) {
  return (
    <div className="bg-card rounded-xl border p-3">
      <p className="flex items-center gap-1.5 text-2xl font-bold tabular-nums">
        {icon}
        {count}
      </p>
      <p className="text-muted-foreground text-xs">{label}</p>
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const createUser = useCreateUser();
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    createUser.mutate(
      {
        username,
        full_name: fullName,
        password,
        is_admin: isAdmin,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setUsername("");
          setFullName("");
          setPhone("");
          setPassword("");
          setIsAdmin(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.admin.addUserTitle}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="new-username">{t.admin.username}</FieldLabel>
              <Input
                id="new-username"
                required
                minLength={3}
                autoComplete="off"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
              <FieldDescription>{t.admin.usernameHint}</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="new-fullname">{t.admin.fullName}</FieldLabel>
              <Input
                id="new-fullname"
                required
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="new-phone">{t.admin.phone}</FieldLabel>
              <Input
                id="new-phone"
                type="tel"
                maxLength={32}
                autoComplete="off"
                value={phone}
                onChange={(event) => setPhone(phoneInput(event.target.value))}
              />
              <FieldDescription>{t.admin.phoneHint}</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="new-password">{t.admin.initialPassword}</FieldLabel>
              <Input
                id="new-password"
                type="text"
                required
                minLength={12}
                autoComplete="off"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <FieldDescription>{t.admin.passwordHint}</FieldDescription>
            </Field>
            <Field orientation="horizontal">
              <FieldLabel htmlFor="new-is-admin">{t.admin.isAdmin}</FieldLabel>
              <Switch id="new-is-admin" checked={isAdmin} onCheckedChange={setIsAdmin} />
            </Field>
            <Button type="submit" disabled={createUser.isPending}>
              {t.admin.addUser}
            </Button>
          </FieldGroup>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({
  user,
  drivenGroups,
  activeGroups,
}: {
  user: User;
  drivenGroups: number;
  /** The ones deactivation would actually pause — paused cars change nothing. */
  activeGroups: number;
}) {
  const { user: me } = useAuth();
  // Deactivation revokes every session at once: on your own row the switch is
  // a self-lockout, and the API refuses it anyway. Another admin does that.
  const isSelf = me?.id === user.id;
  const updateUser = useUpdateUser();
  const resetPassword = useResetPassword();
  const anonymise = useAnonymiseUser();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  return (
    <li className="bg-card flex items-center gap-3 rounded-xl border p-3">
      <UserAvatar user={user} className="size-10" fallbackClassName="bg-secondary font-semibold" />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-sm font-semibold">
          {user.full_name}
          {user.is_admin && (
            <ShieldCheck className="text-primary size-3.5 shrink-0" aria-hidden />
          )}
        </p>
        <p className="text-muted-foreground truncate text-xs">{user.username}</p>
        {/* Tags under the name, not beside it: a row of badges next to the
            buttons would push the switch off a phone screen. */}
        {(drivenGroups > 0 || !user.is_active) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {drivenGroups > 0 && (
              <Badge variant="outline">{t.admin.driverBadge(drivenGroups)}</Badge>
            )}
            {!user.is_active && (
              <Badge variant="secondary">
                {user.is_anonymised ? t.admin.anonymised : t.admin.deactivated}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* An erased account has no identity left to reset, erase or revive. */}
      {!user.is_anonymised && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t.admin.resetPassword}
          onClick={() => setResetOpen(true)}
        >
          <KeyRound aria-hidden />
        </Button>
      )}
      {!user.is_active && !user.is_anonymised && (
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive hover:text-destructive"
          aria-label={t.admin.eraseData}
          title={t.admin.eraseData}
          onClick={() => setEraseOpen(true)}
        >
          <Eraser aria-hidden />
        </Button>
      )}
      {isSelf ? (
        <Badge variant="outline">{t.admin.thatIsYou}</Badge>
      ) : (
        !user.is_anonymised && (
          <Switch
            checked={user.is_active}
            aria-label={t.admin.active}
            onCheckedChange={(next) => {
              if (next) updateUser.mutate({ userId: user.id, input: { is_active: true } });
              else setConfirmOpen(true);
            }}
          />
        )
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.admin.deactivateConfirmTitle(user.full_name)}</DialogTitle>
            <DialogDescription>{t.admin.deactivateConfirmBody}</DialogDescription>
          </DialogHeader>
          {activeGroups > 0 && (
            <p className="text-sm font-medium">{t.admin.deactivateDriverNote(activeGroups)}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              disabled={updateUser.isPending}
              onClick={() =>
                updateUser.mutate(
                  { userId: user.id, input: { is_active: false } },
                  { onSuccess: () => setConfirmOpen(false) },
                )
              }
            >
              {t.admin.deactivate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={eraseOpen} onOpenChange={setEraseOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.admin.eraseConfirmTitle(user.full_name)}</DialogTitle>
            <DialogDescription>{t.admin.eraseConfirmBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEraseOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              disabled={anonymise.isPending}
              onClick={() => anonymise.mutate(user.id, { onSuccess: () => setEraseOpen(false) })}
            >
              {t.admin.erase}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.admin.resetPasswordTitle(user.full_name)}</DialogTitle>
            <DialogDescription>{t.admin.passwordHint}</DialogDescription>
          </DialogHeader>
          <Input
            type="text"
            minLength={12}
            autoComplete="off"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              disabled={resetPassword.isPending || newPassword.length < 12}
              onClick={() =>
                resetPassword.mutate(
                  { userId: user.id, newPassword },
                  {
                    onSuccess: () => {
                      setResetOpen(false);
                      setNewPassword("");
                    },
                  },
                )
              }
            >
              {t.admin.resetPassword}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  );
}

/** "53.69292, 17.51773" (the paste-from-maps format) → a coordinate pair.
 *  Empty input is valid (no coordinates); anything else malformed is not. */
function parseCoords(raw: string): { latitude: number; longitude: number } | null | "invalid" {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/[,;\s]+/).filter(Boolean);
  if (parts.length !== 2) return "invalid";
  const latitude = Number(parts[0]);
  const longitude = Number(parts[1]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return "invalid";
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return "invalid";
  return { latitude, longitude };
}

function AreaFormDialog({
  area,
  open,
  onOpenChange,
}: {
  /** Existing area to edit, or null to create a new one. */
  area: PickupArea | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const createArea = useCreateArea();
  const updateArea = useUpdateArea();
  const [name, setName] = useState(area?.name ?? "");
  const [district, setDistrict] = useState(area?.district ?? "");
  const [coords, setCoords] = useState(
    area && area.latitude !== null && area.longitude !== null
      ? `${area.latitude}, ${area.longitude}`
      : "",
  );

  const parsed = parseCoords(coords);
  const mutation = area ? updateArea : createArea;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (parsed === "invalid") return;
    const done = { onSuccess: () => onOpenChange(false) };
    if (area) {
      // Explicit nulls on edit: an emptied field must clear the stored value.
      // A PATCH that merely omits the key keeps the old district or centroid,
      // which used to leave a wrong centroid route-matching forever.
      updateArea.mutate(
        {
          areaId: area.id,
          input: {
            name: name.trim(),
            district: district.trim() || null,
            latitude: parsed?.latitude ?? null,
            longitude: parsed?.longitude ?? null,
          },
        },
        done,
      );
      return;
    }
    createArea.mutate(
      {
        name: name.trim(),
        ...(district.trim() ? { district: district.trim() } : {}),
        ...(parsed ?? {}),
      },
      done,
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{area ? t.admin.editAreaTitle : t.admin.addAreaTitle}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="area-name">{t.admin.areaName}</FieldLabel>
              <Input
                id="area-name"
                required
                minLength={2}
                maxLength={120}
                autoComplete="off"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t.admin.areaNamePlaceholder}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="area-district">{t.admin.areaDistrict}</FieldLabel>
              <Input
                id="area-district"
                maxLength={80}
                autoComplete="off"
                value={district}
                onChange={(event) => setDistrict(event.target.value)}
                placeholder={t.admin.areaDistrictPlaceholder}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="area-coords">{t.admin.areaCoords}</FieldLabel>
              <Input
                id="area-coords"
                autoComplete="off"
                value={coords}
                onChange={(event) => setCoords(event.target.value)}
                placeholder={t.admin.areaCoordsPlaceholder}
                aria-invalid={parsed === "invalid"}
              />
              <FieldDescription>
                {parsed === "invalid" ? t.admin.areaCoordsInvalid : t.admin.areaCoordsHint}
              </FieldDescription>
            </Field>
            <Button type="submit" disabled={mutation.isPending || parsed === "invalid"}>
              {area ? t.common.save : t.admin.addArea}
            </Button>
          </FieldGroup>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AreaRow({ area }: { area: PickupArea }) {
  const updateArea = useUpdateArea();
  const [editOpen, setEditOpen] = useState(false);

  return (
    <li className="bg-card flex items-center gap-3 rounded-xl border p-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{area.name}</p>
        <p className="text-muted-foreground truncate text-xs">
          {area.district ?? "—"}
          {area.latitude === null && (
            <Badge variant="outline" className="ml-2 align-middle">
              {t.admin.areaNoCoords}
            </Badge>
          )}
        </p>
      </div>

      <Button
        variant="ghost"
        size="icon"
        aria-label={t.admin.editAreaTitle}
        onClick={() => setEditOpen(true)}
      >
        <Pencil aria-hidden />
      </Button>
      <Switch
        checked={area.is_active}
        aria-label={t.admin.areaActive}
        disabled={updateArea.isPending}
        onCheckedChange={(next) =>
          updateArea.mutate({ areaId: area.id, input: { is_active: next } })
        }
      />

      {editOpen && (
        <AreaFormDialog key={area.id} area={area} open={editOpen} onOpenChange={setEditOpen} />
      )}
    </li>
  );
}

function GroupAdminRow({ group }: { group: GroupSummary }) {
  const deleteGroup = useDeleteGroup(t.admin.groupDeleted);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const route = `${townLabel(group.pickup_area)} → ${group.plant.name}`;
  const schedule = `${formatDays(group.days)}, ${formatTime(group.departure_time)}–${formatTime(group.return_time)}`;

  return (
    <li className="bg-card flex items-center gap-3 rounded-xl border p-3">
      <UserAvatar user={group.driver} className="size-10" fallbackClassName="bg-secondary font-semibold" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">
          {group.driver.full_name}
          <span className="text-muted-foreground font-normal"> · {group.car_model}</span>
        </p>
        <p className="text-muted-foreground truncate text-xs">{route}</p>
        <div className="mt-1 flex flex-wrap gap-1">
          <Badge variant="outline">{schedule}</Badge>
          {group.status === "paused" && <Badge variant="secondary">{t.admin.groupPaused}</Badge>}
        </div>
      </div>

      <Button asChild variant="ghost" size="icon" aria-label={t.admin.openGroup} title={t.admin.openGroup}>
        <Link to={`/grupy/${group.id}`}>
          <ExternalLink aria-hidden />
        </Link>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="text-destructive hover:text-destructive"
        aria-label={t.admin.deleteGroup}
        title={t.admin.deleteGroup}
        onClick={() => setConfirmOpen(true)}
      >
        <Trash2 aria-hidden />
      </Button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.admin.deleteGroupConfirmTitle(group.driver.full_name)}</DialogTitle>
            <DialogDescription>
              {route} · {schedule}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm">{t.admin.deleteGroupConfirmBody}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteGroup.isPending}
              onClick={() => deleteGroup.mutate(group.id, { onSuccess: () => setConfirmOpen(false) })}
            >
              {t.admin.deleteGroup}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  );
}

function GroupsTab({ groups }: { groups: GroupSummary[] | undefined }) {
  // By driver, then by departure: one person's morning and afternoon crews
  // sit next to each other.
  const sorted = [...(groups ?? [])].sort(
    (a, b) =>
      a.driver.full_name.localeCompare(b.driver.full_name, "pl") ||
      a.departure_time.localeCompare(b.departure_time),
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">{t.admin.groupsHint}</p>
      {groups && groups.length === 0 && (
        <p className="text-muted-foreground rounded-xl border border-dashed p-6 text-center text-sm">
          {t.admin.noGroups}
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {sorted.map((group) => (
          <GroupAdminRow key={group.id} group={group} />
        ))}
      </ul>
    </div>
  );
}

function AreasTab() {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);
  const { data: areas } = useAdminAreas(debounced);
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">{t.admin.areaManualHint}</p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search
            className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            className="pl-9"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.admin.areaSearchPlaceholder}
            autoComplete="off"
          />
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus aria-hidden /> {t.admin.addArea}
        </Button>
      </div>

      {areas && areas.length === 0 && (
        <p className="text-muted-foreground rounded-xl border border-dashed p-6 text-center text-sm">
          {debounced ? t.admin.areaNoResults : t.admin.noAreas}
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {(areas ?? []).map((area) => (
          <AreaRow key={area.id} area={area} />
        ))}
      </ul>
      {areas && areas.length >= 100 && (
        <p className="text-muted-foreground text-center text-xs">{t.admin.areaMoreResults}</p>
      )}

      {addOpen && <AreaFormDialog area={null} open={addOpen} onOpenChange={setAddOpen} />}
    </div>
  );
}

export function AdminPage() {
  const { data: users } = useAdminUsers();
  const { data: stats } = useAdminStats();
  const { data: groups } = useAdminGroups();
  // Groups per driver: all of them for the "Kierowca" tag, the active ones
  // for what a deactivation would pause.
  const drivenGroups = new Map<string, number>();
  const activeGroups = new Map<string, number>();
  for (const group of groups ?? []) {
    drivenGroups.set(group.driver.id, (drivenGroups.get(group.driver.id) ?? 0) + 1);
    if (group.status === "active") {
      activeGroups.set(group.driver.id, (activeGroups.get(group.driver.id) ?? 0) + 1);
    }
  }
  const { data: requests } = useQuery({
    queryKey: ["admin", "access-requests"],
    queryFn: adminService.listAccessRequests,
    refetchInterval: 60_000,
  });
  const [createOpen, setCreateOpen] = useState(false);
  // Every departed employee ends up an erased "Konto usunięte" row; shown on
  // request only, or they would bury the people the admin is looking for.
  const [showErased, setShowErased] = useState(false);
  const erasedCount = (users ?? []).filter((u) => u.is_anonymised).length;
  const listedUsers = (users ?? []).filter((u) => showErased || !u.is_anonymised);
  // Controlled, because an uncontrolled `defaultValue` is read once at mount —
  // before the requests query has resolved — so "open Zgłoszenia when people
  // are waiting" never fired on a fresh load. Null = follow the data.
  const [tab, setTab] = useState<string | null>(null);
  const activeTab = tab ?? (requests && requests.length > 0 ? "requests" : "users");

  return (
    <div className="animate-in fade-in flex flex-col gap-5 duration-300">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">{t.admin.title}</h1>
      </header>

      <Tabs value={activeTab} onValueChange={setTab}>
        {/* Four tabs do not fit one row on a phone: 2×2 there, one row from sm.
            A tap on the bar pins the current tab — until then the panel follows
            the data (jumps to Zgłoszenia once they load), and a tap on the tab
            already showing would not register, so it jumped away regardless. */}
        <TabsList
          className="grid w-full grid-cols-2 group-data-[orientation=horizontal]/tabs:h-auto sm:inline-flex sm:w-auto sm:group-data-[orientation=horizontal]/tabs:h-9"
          onPointerDown={() => setTab((current) => current ?? activeTab)}
        >
          <TabsTrigger value="users" className="flex-1 sm:flex-none">
            {t.admin.usersTab}
          </TabsTrigger>
          <TabsTrigger value="requests" className="flex-1 sm:flex-none">
            {t.admin.requestsTab}
            {requests && requests.length > 0 && (
              <Badge className="ml-1.5 h-5 min-w-5 rounded-full px-1">{requests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="groups" className="flex-1 sm:flex-none">
            {t.admin.groupsTab}
          </TabsTrigger>
          <TabsTrigger value="areas" className="flex-1 sm:flex-none">
            {t.admin.areasTab}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-4 flex flex-col gap-2">
          {requests && requests.length === 0 && (
            <p className="text-muted-foreground rounded-xl border border-dashed p-6 text-center text-sm">
              {t.admin.noRequests}
            </p>
          )}
          <ul className="flex flex-col gap-2">
            {(requests ?? []).map((request) => (
              <RequestRow key={request.id} request={request} />
            ))}
          </ul>
        </TabsContent>

        <TabsContent value="users" className="mt-4 flex flex-col gap-4">
          {users && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label={t.admin.statActive} count={users.filter((u) => u.is_active).length} />
              <Stat
                label={t.admin.statNotLoggedIn}
                count={users.filter((u) => u.is_active && u.must_change_password).length}
              />
              <Stat
                label={t.admin.statInactive}
                count={users.filter((u) => !u.is_active && !u.is_anonymised).length}
              />
              {stats && (
                <Stat
                  label={t.admin.statCo2(Math.round(stats.km_per_week))}
                  count={Math.round(stats.co2_kg_per_week)}
                  icon={<Leaf className="size-5 text-[oklch(0.55_0.12_124)]" aria-hidden />}
                />
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              onClick={() => {
                // Stay on this tab when the new account lands in the list.
                setTab("users");
                setCreateOpen(true);
              }}
            >
              <Plus aria-hidden /> {t.admin.addUser}
            </Button>
            {erasedCount > 0 && (
              <label className="text-muted-foreground flex items-center gap-2 text-sm">
                <Switch checked={showErased} onCheckedChange={setShowErased} />
                {t.admin.showErased(erasedCount)}
              </label>
            )}
          </div>
          {users && users.length === 0 && (
            <p className="text-muted-foreground text-sm">{t.admin.noUsers}</p>
          )}
          <ul className="flex flex-col gap-2">
            {listedUsers.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                drivenGroups={drivenGroups.get(user.id) ?? 0}
                activeGroups={activeGroups.get(user.id) ?? 0}
              />
            ))}
          </ul>
        </TabsContent>
        <TabsContent value="groups" className="mt-4">
          <GroupsTab groups={groups} />
        </TabsContent>
        <TabsContent value="areas" className="mt-4">
          <AreasTab />
        </TabsContent>
      </Tabs>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
